import { Divider, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CreateTenantWorkOrderSubmissionRequest,
  TenantWorkOrderSubmission,
  UpdateTenantWorkOrderSubmissionRequest,
} from 'vendor-marketplace-shared-library';

import AlertPopup from '../../../../components/common/AlertPopup';
import SubmissionSuccessMessage from '../../../../components/tenant-portal/SubmissionSuccessMessage';
import useAlert from '../../../../hooks/useAlert';
import AttachmentService from '../../../../services/attachment.service';
import { TenantPortalAttachment } from '../../../../types/tenant-portal';
import theme from '../../../../utilities/theme';
import TenantUserDetailsForm from './TenantUserDetailsForm';
import TenantWorkOrderAttachmentSubmissionForm from './TenantWorkOrderAttachmentSubmissionForm';
import TenantWorkOrderDetailsSubmissionForm from './TenantWorkOrderDetailsSubmissionForm';
import tenantWorkOrderSubmissionService from '../../../../services/tenant-work-order-submission.service';

type TenantWorkOrderFormProps = {
  propertyAddress: string;
  hash: string;
};

const TenantWorkOrderFormComponent = (props: TenantWorkOrderFormProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setAlert } = useAlert();
  const [attachments, setAttachments] = useState<TenantPortalAttachment[]>([]);
  const [tenantWorkOrderSubmission, setTenantWorkOrderSubmission] =
    useState<TenantWorkOrderSubmission | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const currentStep = Number(searchParams.get('step'));

  useEffect(() => {
    if (currentStep !== 1 && !tenantWorkOrderSubmission?.id) {
      setSearchParams({ step: '1' });
    }
  }, []);

  const onCreate = async (data: CreateTenantWorkOrderSubmissionRequest) => {
    try {
      setIsLoading(true);
      const submission = await tenantWorkOrderSubmissionService.createTenantWorkOrderSubmission({
        ...data,
        hash: props.hash,
      });
      setTenantWorkOrderSubmission(submission);
      setSearchParams({ step: `${currentStep + 1}` });
      setIsLoading(false);
    } catch (err: any) {
      setIsError(true);
      setAlert({
        message: err.response?.data?.error || 'There was an issue creating your submission.',
        severity: 'error',
      });
    }
  };

  const onUpdate = async (data?: UpdateTenantWorkOrderSubmissionRequest) => {
    try {
      if (tenantWorkOrderSubmission) {
        const submission = await tenantWorkOrderSubmissionService.updateTenantWorkOrderSubmission({
          ...data,
          id: tenantWorkOrderSubmission?.id,
          attachmentIds:
            attachments
              ?.filter(attachment => attachment.id !== null)
              .map(attachment => attachment.id!) || undefined,
        });
        if (submission.submittedAt) {
          setAttachments([]);
          setTenantWorkOrderSubmission(null);
        }
        setTenantWorkOrderSubmission(submission);
        setSearchParams({ step: `${currentStep + 1}` });
      }
    } catch (err: any) {
      setAlert({
        message: err.response?.data?.error || 'There was an issue updating your submission.',
        severity: 'error',
      });
    }
  };

  const onSubmit = async (
    data?: UpdateTenantWorkOrderSubmissionRequest | CreateTenantWorkOrderSubmissionRequest,
  ) => {
    if (tenantWorkOrderSubmission?.id) {
      await onUpdate(data as UpdateTenantWorkOrderSubmissionRequest);
    } else {
      await onCreate(data as CreateTenantWorkOrderSubmissionRequest);
    }
  };

  const onUploadAttachment = async (file: File) => {
    if (file && tenantWorkOrderSubmission) {
      const attachment = {
        file,
        loading: true,
        id: null,
        error: false,
      } as TenantPortalAttachment;

      const attachmentList = [...attachments, attachment];
      setAttachments(attachmentList);

      try {
        const response = await AttachmentService.tenantFileUpload(
          file,
          props.hash,
          tenantWorkOrderSubmission?.id,
        );
        const updatedAttachmentList = attachmentList.map((attachment: any) => {
          if (attachment.file === file) {
            attachment.id = response;
            attachment.loading = false;
          }
          return attachment;
        });

        setAttachments(updatedAttachmentList);
      } catch (error: any) {
        setAlert({
          message: error.response?.data?.error || 'Error uploading attachment.',
          severity: 'error',
        });
        attachment.error = true;
        attachment.loading = false;
        console.error(error);
      }
    }
  };

  const onRemoveAttachment = (attachment: TenantPortalAttachment) =>
    setAttachments(attachments.filter(attach => attach.file !== attachment.file));

  if (currentStep === 4) {
    return (
      <SubmissionSuccessMessage
        title={'Work order submitted'}
        message={
          "A confirmation email has been sent to the email provided. We'll get back to you as soon as possible."
        }
        onClick={() => {
          setSearchParams({ step: '1' });
          setTenantWorkOrderSubmission(null);
        }}
      />
    );
  }

  return (
    <Grid container spacing={4} sx={styles.mainContainer}>
      {/*Header*/}
      <Grid item sx={styles.flexOne}>
        <Grid container sx={styles.headerContainer}>
          <Grid item>
            <Typography variant='h6'>Work order form</Typography>
          </Grid>
          <Grid item>
            <Typography sx={styles.greyText} variant='subtitle2'>
              {props.propertyAddress}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item sx={styles.flexOne}>
        <Divider />
      </Grid>

      {/*Form*/}
      {currentStep === 1 && (
        <Grid item sx={styles.flexOne}>
          <TenantWorkOrderDetailsSubmissionForm
            onSubmit={onSubmit}
            tenantWorkOrderSubmission={tenantWorkOrderSubmission}
            isLoading={isLoading}
            isError={isError}
          />
        </Grid>
      )}
      {currentStep === 2 && (
        <Grid item>
          <TenantWorkOrderAttachmentSubmissionForm
            setTenantWorkOrderSubmission={setTenantWorkOrderSubmission}
            tenantWorkOrderSubmission={tenantWorkOrderSubmission}
            onUpload={onUploadAttachment}
            onSubmit={onSubmit}
            attachments={attachments}
            onRemove={onRemoveAttachment}
          />
        </Grid>
      )}
      {currentStep === 3 && (
        <Grid item>
          <TenantUserDetailsForm
            onSubmit={onSubmit}
            tenantWorkOrderSubmission={tenantWorkOrderSubmission}
            loading={isLoading}
          />
        </Grid>
      )}
      <AlertPopup />
    </Grid>
  );
};

export default TenantWorkOrderFormComponent;

const styles = {
  mainContainer: {
    flexDirection: 'column',
    flex: 1,
    maxWidth: '600px',
  },
  greyText: {
    color: theme.palette.secondary.main,
  },
  headerContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  flexOne: {
    flex: 1,
  },
};
